// src/piiFields.ts
/**
* Master PII field registry for the Dottie agent surface — one source of truth for the encryption
* layer (SIM-1403) and the Sentry scrubber (SIM-1404). Fields are `<Model>.<column>`, matching
* Prisma names exactly, with two axes: `encrypt` (stored encrypted at rest) and `scrub` (must be
* removed from any Sentry envelope before send).
*/
/**
* INVARIANT: anything `encrypt: true` MUST also be `scrub: true`. The reverse is not required —
* `piiColumnsTouched` is audit-logged unencrypted as column NAMES, never values, but stays out of
* Sentry as defense in depth. When adding a PII-bearing column, extend this registry FIRST, then
* run the spec to confirm it flows through both layers. SIM-1177 shares the union.
*/
const DOTTIE_PII_FIELD_REGISTRY = [
	{
		name: "AgentMessage.content",
		encrypt: true,
		scrub: true
	},
	{
		name: "AgentMessage.guardrailInput",
		encrypt: true,
		scrub: true
	},
	{
		name: "AgentMessage.guardrailOutput",
		encrypt: true,
		scrub: true
	},
	{
		name: "AgentMessage.intent",
		encrypt: true,
		scrub: true
	},
	{
		name: "AgentMessage.rich",
		encrypt: true,
		scrub: true
	},
	{
		name: "AgentSession.title",
		encrypt: true,
		scrub: true
	},
	{
		name: "AgentAuditLog.sql",
		encrypt: true,
		scrub: true
	},
	{
		name: "AgentAuditLog.piiColumnsTouched",
		encrypt: false,
		scrub: true
	}
];
/** Names of fields that are stored encrypted at rest. */
const DOTTIE_ENCRYPTED_FIELDS = DOTTIE_PII_FIELD_REGISTRY.filter((f) => f.encrypt).map((f) => f.name);
/** Names of fields that must be scrubbed from Sentry envelopes before send. */
const DOTTIE_SCRUB_FIELDS = DOTTIE_PII_FIELD_REGISTRY.filter((f) => f.scrub).map((f) => f.name);
/** Bare column names (the part after the dot) — used by the scrubber's tree walker. */
const DOTTIE_SCRUB_COLUMN_NAMES = Array.from(new Set(DOTTIE_SCRUB_FIELDS.map((qualified) => qualified.split(".")[1] ?? qualified)));
// src/sentryScrubber.ts
/**
* POLICY, set 2026-06-03: no chat prompt or response data goes to Sentry,
* full stop. This module is the wire-level enforcement of that rule.
*/
/**
* Operates on the event shape WITHOUT importing a Sentry SDK, so both
* `@sentry/node` (platform) and `@sentry/react` (portal) can call it from
* `beforeSend`. See history: SIM-1404, SIM-1740.
*/
/**
* Three layers, in order: a field-name allowlist from
* `DOTTIE_SCRUB_COLUMN_NAMES` replacing matched values at any depth; a regex
* pass over strings, since chat shape leaks into Anthropic errors and SQL
* literals; then >200-char `extra` strings truncated as the catch-all.
*/
/**
* Deliberately untouched: stack frames, error class names, tag values, and
* shape metadata (messageCount, roundIndex, toolName, `*Sha` hashes).
*/
const LONG_STRING_THRESHOLD = 200;
/**
* Carrier keys for the T4 session-context envelope, nuked whole at any depth:
* `t4SnapshotCiphertext` (encrypted — still never let it through) and
* `sessionContext` (plaintext, as carried on the chat request). The walk below
* also matches the envelope by SHAPE, for when it rides under another key.
*/
const T4_SCRUB_COLUMN_NAMES = ["t4SnapshotCiphertext", "sessionContext"];
const SCRUB_FIELD_SET = /* @__PURE__ */ new Set([...DOTTIE_SCRUB_COLUMN_NAMES, ...T4_SCRUB_COLUMN_NAMES]);
/**
* Patterns that catch chat-shape leakage in string error messages. Each
* pattern replaces just the leaked content portion, preserving the
* surrounding diagnostic frame so the error stays debuggable.
*/
const REGEX_PATTERNS = [
	{
		pattern: /(Question:\s*)"([^"]+)"/g,
		replacement: "$1\"[REDACTED:chat-content]\""
	},
	{
		pattern: /(prompt(?:\s+was)?:?\s*)"([^"]+)"/gi,
		replacement: "$1\"[REDACTED:chat-content]\""
	},
	{
		pattern: /(\bWHERE\s+\w+\s*[=<>!]+\s*)'([^']+)'/gi,
		replacement: "$1'[REDACTED:sql-literal]'"
	},
	{
		pattern: /(\bIN\s*\()([^)]+)(\))/gi,
		replacement: "$1[REDACTED:sql-literal]$3"
	}
];
/**
* Scrub a Sentry event in-place-by-rebuild, returning the scrubbed event or
* `null` to drop the event entirely. Currently this scrubber never drops —
* it always returns a (possibly modified) event so stack traces remain
* available for triage.
*/
function scrubDottieEvent(event) {
	const afterFieldScrub = scrubByFieldName(event);
	scrubT4Envelopes(afterFieldScrub);
	applyStringHeuristics(afterFieldScrub);
	return afterFieldScrub;
}
/**
* Recursively walk an arbitrary JSON-like value, replacing any object key
* whose name is in `SCRUB_FIELD_SET` with `[REDACTED:<key>]`. Arrays are
* walked element-wise; other primitives pass through.
*/
function scrubByFieldName(value) {
	if (Array.isArray(value)) return value.map((v) => scrubByFieldName(v));
	if (value !== null && typeof value === "object") {
		const out = {};
		for (const [key, child] of Object.entries(value)) {
			if (SCRUB_FIELD_SET.has(key)) {
				out[key] = `[REDACTED:${key}]`;
				continue;
			}
			out[key] = scrubByFieldName(child);
		}
		return out;
	}
	return value;
}
function asObject(value) {
	return value !== null && typeof value === "object" && !Array.isArray(value) ? value : null;
}
/**
* Walk the (already field-scrubbed) event tree and scrub any value that matches
* the `SessionContextV1` shape. Matched envelopes have their PII members
* scrubbed in place; structural metadata (`config.boundDatasets`, `cardConfig`,
* `dataSpec`, ids, types) is left untouched. Recurses through arrays + objects.
*/
function scrubT4Envelopes(value) {
	if (Array.isArray(value)) {
		for (const item of value) scrubT4Envelopes(item);
		return;
	}
	const obj = asObject(value);
	if (obj === null) return;
	if (isSessionContextV1(obj)) scrubSessionContextState(asObject(asObject(obj.dashboard)?.state));
	for (const child of Object.values(obj)) scrubT4Envelopes(child);
}
/** Structural match for `SessionContextV1` — version tag + dashboard.state. */
function isSessionContextV1(obj) {
	if (obj.version !== "v1") return false;
	const dashboard = asObject(obj.dashboard);
	return dashboard !== null && asObject(dashboard.state) !== null;
}
/**
* Scrubs the per-message `state`: filter values, and a focused row's
* `identityValue` and `rowSnapshot`. `identityValue` is the row identity — a
* student name or ID — and is FERPA PII. `identityField` is the column name,
* structural, and is deliberately preserved.
*/
function scrubSessionContextState(state) {
	if (state === null) return;
	if (Array.isArray(state.activeFilters)) for (const filter of state.activeFilters) {
		const f = asObject(filter);
		if (f !== null && "value" in f) f.value = "<scrubbed>";
	}
	const focused = asObject(state.focused);
	if (focused?.kind === "row") {
		if (typeof focused.identityValue === "string") focused.identityValue = "<scrubbed>";
		const snapshot = asObject(focused.rowSnapshot);
		if (snapshot !== null) focused.rowSnapshot = `<scrubbed:${Object.keys(snapshot).length} keys>`;
	}
}
/**
* Apply Layer 2 (regex heuristics) and Layer 3 (long-string flag) to the
* known string-bearing locations in a Sentry event. Mutates in place — the
* top-level scrubber already cloned the object via the field-name walker.
*/
function applyStringHeuristics(event) {
	if (event.message !== void 0) event.message = scrubStringForSentry(event.message, "message");
	if (event.exception?.values) {
		for (const entry of event.exception.values) if (entry.value !== void 0) entry.value = scrubStringForSentry(entry.value, "exception.value");
	}
	if (event.breadcrumbs) for (const crumb of event.breadcrumbs) {
		if (crumb.message !== void 0) crumb.message = scrubStringForSentry(crumb.message, "breadcrumb.message");
		if (crumb.data) sanitizeRecord(crumb.data);
	}
	if (event.extra) sanitizeRecord(event.extra);
	if (event.contexts) sanitizeRecord(event.contexts);
}
function sanitizeRecord(record) {
	for (const key of Object.keys(record)) {
		const value = record[key];
		if (typeof value === "string") record[key] = scrubStringForSentry(value, key);
	}
}
/**
* Layer-2 regex then layer-3 truncation for one string; `context` only
* annotates the truncation marker. Exported as the supported escape hatch for
* the SIM-1177 PII linter on `Sentry.setContext`: bind the value outside the
* call, scrub it, then pass the result in.
*/
function scrubStringForSentry(value, context) {
	let out = value;
	for (const { pattern, replacement } of REGEX_PATTERNS) out = out.replace(pattern, replacement);
	if (out.length > LONG_STRING_THRESHOLD) out = `${out.slice(0, LONG_STRING_THRESHOLD)}…[TRUNCATED:long-string:${context}]`;
	return out;
}
export { DOTTIE_SCRUB_COLUMN_NAMES as a, DOTTIE_PII_FIELD_REGISTRY as i, scrubStringForSentry as n, DOTTIE_SCRUB_FIELDS as o, DOTTIE_ENCRYPTED_FIELDS as r, scrubDottieEvent as t };

//# sourceMappingURL=sentryScrubber.mjs.map